import {
	IconButton,
	TextInput,
	TextInputReadOnly,
	ValidationError
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import { ReactElement, useCallback } from "react";
import styles from "./KeyValueCollection.module.less";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import { KeyValuePair } from "client/Api/common";
import { InputWithSpecialSyntax } from "./InputWithSpecialSyntax";

export type KeyValueCollectionProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<KeyValuePair<string, string>>[]>;
	maxAmount: number;
	isReadOnly?: boolean;
};

export const KeyValueCollection = (props: KeyValueCollectionProps): ReactElement => {
	const {
		attachable,
		maxAmount,
		isReadOnly = false
	} = props;

	const addHandler = useCallback(
		(): void => {
			const item: KeyValuePair<string, string> = { key: "", value: "" };

			attachable.reducer
				.push(setClientId(item));

			attachable.validation
				.triggerInputFinished();
		},
		[attachable.reducer, attachable.validation]
	);

	const removeHandler = useCallback(
		(index: number): void => {
			attachable
				.reducer
				.removeAt(index);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable.reducer, attachable.validation]
	);

	return (
		<div
			{...setTrackingId(attachable.reducer.path)}
			className={styles.root}
		>
			<ul className={styles.list}>
				{attachable.map((x, i): ReactElement => (
					<li key={getClientId(x.value)}>
						<Item
							attachable={x}
							onRemove={() => removeHandler(i)}
							isReadOnly={isReadOnly}
						/>
						<ValidationError {...x.attachToValidationError(true)} />
					</li>
				))}
			</ul>

			{(!isReadOnly && attachable.value.length < maxAmount) && (
				<IconButton
					kind="dashed"
					width="fill"
					icon="plus"
					onClick={addHandler}
					trackingId="add"
				/>
			)}
		</div>
	);
};
KeyValueCollection.displayName = "KeyValueCollection";

type ItemProps = {
	attachable: IValueInputAttachable<ObjectWithClientId<KeyValuePair<string, string>>>;
	isReadOnly: boolean;
	onRemove: () => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		attachable,
		isReadOnly,
		onRemove
	} = props;

	return (
		<div
			{...setTrackingId(attachable.reducer.path)}
			className={styles.item}
		>
			<div className={styles.input}>
				{isReadOnly
					? (
						<TextInputReadOnly {...attachable.for("key").attachToTextInput()} />
					)
					: (
						<>
							<TextInput
								{...attachable.for("key").attachToTextInput()}
								placeholder={localize("_EnterKey")}
							/>
							<ValidationError {...attachable.for("key").attachToValidationError()} />
						</>
					)
				}
			</div>

			<div className={styles.input}>
				{isReadOnly
					? (
						<TextInputReadOnly {...attachable.for("value").attachToTextInput()} />
					)
					: (
						<>
							<InputWithSpecialSyntax kind="placholder">
								<TextInput
									{...attachable.for("value").attachToTextInput()}
									placeholder={localize("_EnterValue")}
								/>
							</InputWithSpecialSyntax>
							<ValidationError {...attachable.for("value").attachToValidationError()} />
						</>
					)
				}
			</div>

			{!isReadOnly && (
				<IconButton
					kind="secondary"
					icon="minus"
					onClick={onRemove}
					trackingId="remove"
				/>
			)}
		</div>
	);
};
Item.displayName = "KeyValueCollection.Item";
